import { EventEmitter, Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { sendEmailVerification, UserProfile } from '@angular/fire/auth';
import firebase from 'firebase/compat/app';
import { User } from '../models/user.model';
import { UserService } from './user.service';
import { Logger } from '../logger.service';
// import { AUTH_ERROR_CODES_MAP } from '../utilities/fire-auth-errors';
import { CredentialsService } from '@app/auth';
import { CustomerService } from './customer.service';
import { Customer } from '../models/customer.model';
import { FileService } from './file.service';
import { Functions } from '@angular/fire/functions';
import { httpsCallable } from 'firebase/functions';
import { environment } from '@env/environment';
import { AlertController } from '@ionic/angular';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { serverTimestamp } from 'firebase/firestore';
import { IpApiService } from './ip-api/ip-api.service';
const log = new Logger('Auth Service');
// const authErrors = AUTH_ERROR_CODES_MAP;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  userChange = new EventEmitter<User | null>();
  customerChange = new EventEmitter<Customer | null>();
  fireUserChange = new EventEmitter<firebase.User | null>();

  fireUser: firebase.User | null = null;
  user?: User;
  customer?: Customer;
  numberVerified = false;
  private isUpdatingToken = false;
  private tokenUpdatePromise: Promise<string | null> | null = null;
  private tokenUpdateInterval?: any;
  private lastProcessedUid: string | null = null;
  private isProcessingAuthState = false;

  constructor(
    private auth: AngularFireAuth,
    private userS: UserService,
    private customerS: CustomerService,
    private credentialsService: CredentialsService,
    private fileS: FileService,
    private functions: Functions,
    private alertCtrl: AlertController,
    private http: HttpClient,
    private ipS: IpApiService
  ) {
    void this.checkUser();
  }

  // Public functions
  // =================================

  async signUpEmail(
    email: string,
    password: string,
    customer: Customer,
    user: User
  ): Promise<{ ok: boolean; data?: Customer; message: string; code?: string }> {
    try {
      // const url = environment.api.customers.url;

      // const body = {
      //   email,
      //   password,
      //   user,
      //   customer,
      // };
      // const response = (await firstValueFrom(this.http.post(url, body))) as {
      //   ok: boolean;
      //   message: string;
      //   data?: Customer;
      // };

      // return response;
      const userF = await this.auth.createUserWithEmailAndPassword(email, password);
      this.fireUser = userF.user;
      if (this.fireUser) {
        user.uid = this.fireUser.uid;
        const addUser = await this.userS.create(user);
        customer.uid = user.uid;
        const addCustomer = await this.customerS.create(customer);

        if (addUser.success && addCustomer.success) {
          this.user = user;
          this.customer = customer;

          return {
            ok: true,
            data: customer,
            message: 'Cliente registrado correctamente',
          };
        } else {
          await this.userS.delete(user.uid);
          await this.customerS.delete(user.uid);
          await this.fireUser.delete();
          return {
            ok: false,
            message: 'Error creando datos del cliente',
          };
        }
      } else {
        return {
          ok: false,
          message: 'Error creando usuario',
        };
      }
    } catch (error: any) {
      log.error(error.code);
      return {
        ok: false,
        message: error.message,
        code: error.code, // Incluir el código de error para traducción
      };
    }
  }

  async logInEmail(email: string, password: boolean, remember: boolean): Promise<boolean | 'EMAIL_NOT_VERIFIED'> {
    try {
      this.auth.setPersistence(remember ? 'local' : 'session');
      const credentials = await this.auth.signInWithEmailAndPassword(email, password as any);

      if (credentials.user) {
        this.fireUser = credentials.user;
        const customer = await this.customerS.getById(this.fireUser.uid);
        let user = await this.userS.getById(this.fireUser.uid);

        if (user) {
          user = await this.userS.updateLoginAt(user);
        }

        if (user && customer) {
          this.user = user;
          this.customer = customer;

          if (this.customer.uid !== this.fireUser.uid) {
            await this.customerS.update(this.fireUser.uid, { uid: this.fireUser.uid });
            this.customer.uid = this.fireUser.uid;
          }

          // Obtener token sin forzar renovación (Firebase lo renueva automáticamente si está expirado)
          const token = await this.fireUser.getIdToken(false);

          // Verificar si el email está verificado en Firebase Auth o en la BD
          const firebaseEmailVerified = this.fireUser.emailVerified;
          const dbEmailVerified = this.customer.emailVerified;

          // Si Firebase Auth tiene emailVerified pero la BD no, actualizar la BD
          if (firebaseEmailVerified && !dbEmailVerified) {
            await this.customerS.update(this.fireUser.uid, {
              emailVerified: true,
              modifiedAt: serverTimestamp() as any,
            });
            this.customer.emailVerified = true;
            log.info('Email verificado sincronizado desde Firebase Auth a BD', { uid: this.fireUser.uid });
          }

          // Si ninguno tiene emailVerified, bloquear acceso y enviar correo
          if (!firebaseEmailVerified && !dbEmailVerified) {
            // Enviar correo de verificación usando HttpClient con token de autenticación
            try {
              const url = environment.api.mailSend.urlVerify;
              const body = {
                email: this.customer.email,
              };

              const response = await firstValueFrom(
                this.http.post(url, body, {
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                })
              );

              // Verificar si la respuesta es exitosa
              const isSuccess = response && ((response as any).success === true || (response as any).code === '200');

              if (isSuccess) {
                log.info('Correo de verificación enviado en login', { email: this.customer.email });
              }
            } catch (error) {
              log.error('Error enviando correo de verificación en login', error);
            }

            // Cerrar sesión antes de mostrar el alerta
            await this.logOut();

            // Mostrar alerta siempre, independientemente del resultado del envío
            const alert = await this.alertCtrl.create({
              header: 'Verificación de correo requerida',
              message: 'Te enviamos un correo de verificación. Por favor verifica tu correo y vuelve a entrar.',
              buttons: ['OK'],
            });
            await alert.present();

            // Retornar un valor especial para indicar que el email no está verificado
            return 'EMAIL_NOT_VERIFIED';
          }

          this.userChange.emit(this.user);
          this.customerChange.emit(this.customer);

          // Usar emailVerified de la BD (prioridad) o Firebase Auth como fallback
          const finalEmailVerified = this.customer.emailVerified || this.fireUser.emailVerified;

          this.credentialsService.setCredentials(
            {
              id: this.fireUser.uid,
              name: this.user.displayName,
              token,
              email: this.customer.email,
              phone: this.customer.phoneNumber,
              emailVerified: finalEmailVerified,
              phoneVerified: this.customer.phoneVerified,
              idVerified: this.customer.idVerified,
              idChecking: (await this.fileS.getFilesFromPath(`accounts/${user.uid}/id`)).length === 2,
              user,
              customer,
            },
            remember
          );
          localStorage.setItem('remember', JSON.stringify(remember));

          // Obtener links de casino y book después de login exitoso
          // Ejecutar de forma asíncrona sin bloquear el login
          if (this.fireUser) {
            setTimeout(() => {
              this.loadIframeLinks(this.fireUser!.uid, token).catch((error) => {
                log.error('Error al cargar links de iframe después de login:', error);
              });
            }, 500); // Pequeño delay para asegurar que las credenciales estén completamente establecidas
          }

          return true;
        } else {
          await this.logOut();
          return false;
        }
      } else {
        return false;
      }
    } catch (error: any) {
      log.error(error);

      throw error;
    }
  }

  async logOut(): Promise<void> {
    await this.auth.signOut();
    this.fireUser = null;
    this.user = undefined;
    this.customer = undefined;

    this.credentialsService.setCredentials();
    localStorage.removeItem('remember');
    // Limpiar links de iframe al cerrar sesión
    localStorage.removeItem('casino-iframe-url');
    localStorage.removeItem('book-iframe-url');
    this.userChange.emit(undefined);
    this.customerChange.emit(undefined);
    this.fireUserChange.emit(undefined);
  }

  async sendRestorePasswordMail(email: string): Promise<boolean> {
    try {
      const url = environment.api.mailSend.urlWithoutAuth;
      const response = await firstValueFrom(
        this.http.post<any>(
          url,
          {
            email: email,
            templateId: 'd-0045310fc2574eb0a4385473734f34ef',
            dynamicTemplateData: {
              link: environment.urlApp,
            },
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )
      );
      return !!(response && (response.code === '200' || response.success));
    } catch (error) {
      log.error(error);
      return false;
    }
  }

  getCustomerId(): string | null {
    return this.customer?.uid || this.credentialsService.credentials?.id || null;
  }

  async sendVerificationMail(): Promise<boolean> {
    try {
      const user = await this.auth.currentUser;
      const email = user?.email || this.customer?.email;
      if (email) {
        const url = environment.api.mailSend.urlWithoutAuth;
        const response = await firstValueFrom(
          this.http.post<any>(
            url,
            {
              email: email,
              templateId: 'd-6812fe2a16794b85a2670208058be8d3',
              dynamicTemplateData: {
                link: environment.urlApp,
              },
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          )
        );
        return !!(response && (response.code === '200' || response.success));
      }
    } catch (error) {
      log.error(error);
    }
    return false;
  }

  async updateProfile(profile: UserProfile): Promise<boolean> {
    try {
      await this.fireUser?.updateProfile(profile);
      return true;
    } catch (error) {
      log.error(error);
      return false;
    }
  }

  async updateMail(newMail: string): Promise<boolean> {
    try {
      await this.fireUser?.updateEmail(newMail);
      return true;
    } catch (error) {
      log.error(error);
      return false;
    }
  }

  async updatePassword(newPassword: string): Promise<boolean> {
    try {
      await this.fireUser?.updatePassword(newPassword);
      return true;
    } catch (error) {
      log.error(error);
      return false;
    }
  }

  // async sendPhoneSMS(phone: string, verifier: firebase.auth.ApplicationVerifier) {
  //   try {
  //     log.info(phone);
  //     const result = await this.auth.signInWithPhoneNumber(phone, verifier);
  //     return result;
  //   } catch (error) {
  //     log.error(error);
  //     return null;
  //   }
  // }

  async sendPhoneSMS(phone: string, verifier: firebase.auth.ApplicationVerifier) {
    try {
      const current = await this.auth.currentUser;
      if (!current) throw new Error('no-current-user');
      // IMPORTANTE: iniciar el flujo de VINCULACIÓN
      const result = await current.linkWithPhoneNumber(phone, verifier);
      return result; // ConfirmationResult
    } catch (error) {
      log.error(error);
      return null;
    }
  }

  // async verifyPhoneSMS(code: string, result: firebase.auth.ConfirmationResult) {
  //   this.numberVerified = true;
  //   const user = await result.confirm(code);
  //   log.info(user.credential?.toJSON());
  //   if (user.credential) {
  //     this.fireUser?.updatePhoneNumber(user.credential);
  //   }
  //   return user;
  // }

  async verifyPhoneSMS(code: string, result: firebase.auth.ConfirmationResult) {
    const current = await this.auth.currentUser;
    if (!current) throw new Error('no-current-user');

    // Construye la credencial de teléfono con el verificationId que ya tienes:
    const cred = firebase.auth.PhoneAuthProvider.credential(result.verificationId, code);

    // VINCULA la credencial al usuario que ya está logueado
    const linked = await current.linkWithCredential(cred);

    // refresca el usuario en memoria
    await current.reload();
    this.fireUser = current;

    return linked; // UserCredential
  }

  createRecaptcha(): firebase.auth.RecaptchaVerifier {
    if ((window as any)._recaptchaVerifier) {
      try {
        (window as any)._recaptchaVerifier.clear();
      } catch {}
      delete (window as any)._recaptchaVerifier;
    }

    (window as any).recaptchaSolved = false;
    (window as any)._recaptchaWidgetId = undefined;

    const app = firebase.app();
    const verifier = new firebase.auth.RecaptchaVerifier(
      'recaptcha-container',
      {
        size: 'normal',
        callback: () => {
          (window as any).recaptchaSolved = true;
          window.dispatchEvent(new Event('recaptcha-ok'));
        },
        'expired-callback': () => {
          (window as any).recaptchaSolved = false;
        },
      },
      app
    );

    (window as any)._recaptchaVerifier = verifier;
    return verifier;
  }

  // async restorePassword(newPassword: string): Promise<boolean> {

  // }

  // Private functions
  // =================================

  private clearSession() {
    this.user = undefined;
    this.customer = undefined;
    this.fireUser = null;
    this.credentialsService.setCredentials();
    this.userChange.emit(null);
    this.customerChange.emit(null);
    this.fireUserChange.emit(null);
  }

  private async checkUser(): Promise<void> {
    this.auth.onAuthStateChanged(async (user) => {
      // Evitar procesar el mismo estado de usuario múltiples veces
      const currentUid = user?.uid || null;
      if (this.isProcessingAuthState && this.lastProcessedUid === currentUid) {
        return; // Ya estamos procesando este estado
      }

      this.isProcessingAuthState = true;
      this.lastProcessedUid = currentUid;

      try {
        this.fireUser = user;

        if (this.fireUser) {
          // Si el usuario es nuevo o cambió
          if (!this.user || this.fireUser.uid !== this.user.uid) {
            const customer = await this.customerS.getById(this.fireUser.uid);
            const userId = await this.userS.getById(this.fireUser.uid);

            if (userId && customer) {
              this.user = userId;
              this.customer = customer;

              // Sincronizar emailVerified: solo actualizar BD si Firebase Auth tiene true y BD tiene false
              // No sobrescribir si la BD ya tiene true (prioridad a la BD)
              if (this.fireUser.emailVerified && !this.customer.emailVerified) {
                await this.customerS.update(this.fireUser.uid, {
                  emailVerified: true,
                  modifiedAt: serverTimestamp() as any,
                });
                this.customer.emailVerified = true;
                log.info('Email verificado sincronizado desde Firebase Auth a BD en checkUser', {
                  uid: this.fireUser.uid,
                });
              }

              if (typeof this.customer.documentVerified === 'undefined') {
                await this.customerS.update(this.fireUser.uid, {
                  documentVerified: false,
                  documentRejected: false,
                  idRejected: false,
                });
                this.customer.documentVerified = false;
                this.customer.documentRejected = false;
                this.customer.idRejected = false;
              }

              // Obtener token sin forzar renovación (Firebase lo renueva automáticamente si está expirado)
              // Solo forzar renovación si realmente no hay token
              const existingToken = this.credentialsService.credentials?.token;
              const token = existingToken
                ? await this.fireUser.getIdToken(false) // No forzar si ya tenemos token
                : await this.fireUser.getIdToken(true); // Solo forzar si no hay token

              // Usar emailVerified de la BD (prioridad) o Firebase Auth como fallback
              const finalEmailVerified = this.customer.emailVerified || this.fireUser.emailVerified;

              this.credentialsService.setCredentials(
                {
                  id: this.fireUser.uid,
                  name: this.user.displayName,
                  token,
                  email: this.customer.email,
                  phone: this.customer.phoneNumber,
                  emailVerified: finalEmailVerified,
                  phoneVerified: this.customer.phoneVerified,
                  idVerified: this.customer.idVerified,
                  idChecking: (await this.fileS.getFilesFromPath(`accounts/${customer.uid}/id`)).length === 2,
                  user: userId,
                  customer,
                },
                JSON.parse(localStorage.getItem('remember') ?? 'null')
              );
              this.userChange.emit(this.user);
              this.customerChange.emit(this.customer);
              this.fireUserChange.emit(user);
            } else {
              this.clearSession();
            }
          }
        } else {
          this.clearSession();
        }
      } finally {
        this.isProcessingAuthState = false;
      }
    });

    // Actualizar token periódicamente (cada ~17 minutos, antes de que expire)
    // Limpiar intervalo anterior si existe para evitar múltiples intervalos
    if (this.tokenUpdateInterval) {
      clearInterval(this.tokenUpdateInterval);
    }

    this.tokenUpdateInterval = setInterval(() => {
      // No forzar renovación en actualizaciones periódicas
      // Firebase renovará automáticamente si el token está próximo a expirar
      this.updateToken(false);
    }, 1035000);
  }

  async signInLink(email: string, link: string) {
    // si ya se realizo el envio de correo
    if (await this.auth.isSignInWithEmailLink(link)) {
      const credentials = await this.auth.signInWithEmailLink(email, link);
      if (credentials.user) {
        log.info('Login correcto');
      } else {
        log.info('Error login');
      }
    }

    //si aun no se realiza el proceso, enviar correo
    else {
      this.auth.sendSignInLinkToEmail(email, {
        url: link,
      });
    }
  }

  async updateToken(forceRefresh: boolean = false): Promise<string | null> {
    // Si ya hay una actualización en curso, retornar la misma promesa
    if (this.isUpdatingToken && this.tokenUpdatePromise) {
      return this.tokenUpdatePromise;
    }

    this.isUpdatingToken = true;
    this.tokenUpdatePromise = (async () => {
      try {
        const user = await this.auth.currentUser;
        const credentials = this.credentialsService.credentials;

        if (user && credentials) {
          // Solo forzar renovación si realmente es necesario (peticiones críticas)
          // Firebase maneja automáticamente la renovación si el token está expirado
          const token = await user.getIdToken(forceRefresh);

          // Solo actualizar si el token cambió para evitar actualizaciones innecesarias
          if (token !== credentials.token) {
            this.credentialsService.setCredentials(
              {
                ...credentials,
                token,
              },
              JSON.parse(localStorage.getItem('remember') ?? 'null')
            );
          }

          return token;
        }
      } catch (error) {
        log.error(error);
      } finally {
        this.isUpdatingToken = false;
        this.tokenUpdatePromise = null;
      }
      return null;
    })();

    return this.tokenUpdatePromise;
  }

  checkUserClaims(email: string): Promise<{ migrate: boolean; password: boolean }> {
    const callable = httpsCallable(this.functions, 'checkUserClaims');
    return callable({ email }).then((result) => result.data as any);
  }

  async setPasswordClaim(email: string): Promise<boolean> {
    try {
      // Llamada a función en la nube (la función puede obtener el userId del email)
      const callable = httpsCallable(this.functions, 'setPasswordClaim');
      const result = await callable({ email });

      // Si tenemos un usuario autenticado, actualizar el campo tempPassword localmente
      const userId = this.fireUser?.uid;
      if (userId && (result.data as { success: boolean }).success) {
        try {
          await this.customerS.update(userId, { tempPassword: 'change', uid: userId });
        } catch (updateErr) {
          // Si falla la actualización local, no es crítico ya que la función en la nube ya lo maneja
          log.warn('No se pudo actualizar tempPassword localmente:', updateErr);
        }
      }

      return (result.data as { success: boolean }).success;
    } catch (err) {
      log.error('setPasswordClaim failed:', err);
      return false;
    }
  }

  /**
   * Obtiene y guarda los links de iframe para casino y book en localStorage
   * Se ejecuta automáticamente después de un login exitoso
   */
  private async loadIframeLinks(playerId: string, token: string): Promise<void> {
    // Esperar un momento para asegurar que las credenciales estén completamente establecidas
    await new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      // Obtener link de casino usando HttpClient directamente
      try {
        const { url, getFrame } = environment.casino.cloudApi;
        const casinoUrl = `${url}/${getFrame}`;
        const casinoResponse = (await firstValueFrom(
          this.http.post(
            casinoUrl,
            { playerId },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          )
        )) as any;

        if (casinoResponse && casinoResponse.url) {
          localStorage.setItem('casino-iframe-url', casinoResponse.url);
          log.info('Link de casino guardado en storage después de login');
        }
      } catch (error) {
        log.error('Error al obtener link de casino después de login:', error);
      }

      // Obtener link de book usando HttpClient directamente
      try {
        const ip = await this.ipS.getIp();
        if (ip) {
          const bookUrl = `${environment.api.book.url}${environment.api.book.paths.login}`;
          const bookResponse = (await firstValueFrom(
            this.http.post(
              bookUrl,
              { ip: ip.query },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              }
            )
          )) as any;

          if (bookResponse && bookResponse.Link) {
            localStorage.setItem('book-iframe-url', bookResponse.Link);
            log.info('Link de book guardado en storage después de login');
          }
        }
      } catch (error) {
        log.error('Error al obtener link de book después de login:', error);
      }
    } catch (error) {
      log.error('Error al cargar links de iframe después de login:', error);
    }
  }
}
